import * as S from './ResolveSwapOrReturnContainer.styles'
import { Box, Typography } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { actions } from '../../SwapOrReturnState'
import { useDispatch } from 'react-redux'
import { useSelector } from 'src/utils/store'
import {
  PendingData, SwapOrReturnStatusEnum, SwapOrReturnTypeIds, SwapOrReturnTypesEnum,
} from '../../SwapOrReturnTypes'
import { toast } from 'react-toastify'
import ResolveSwapOrReturnTable from '../ResolveSwapOrReturnTable'
import ConfirmOrRejectModal from 'src/features/base/components/ConfirmOrRejectModal'
import { useHistory } from 'react-router-dom'
import { getReceivementAgreementInfo } from 'src/utils/getReceivementAgreementInfo'
import { useFulfillmentCenter } from 'src/hooks/useFulfillmentCenter'

interface ResolveSwapOrReturnContainerProps {
  useAs?: 'page' | 'component'
  stopResolution?: () => void
}

export const ResolveSwapOrReturnContainer = ({
  useAs = 'page', stopResolution,
}: ResolveSwapOrReturnContainerProps) => {
  const { fc, validateFc } = useFulfillmentCenter()

  const dispatch = useDispatch()

  const history = useHistory()

  const { swapOrReturnToResolveIds, swapOrReturnToResolveData } = useSelector((state) => state.swapOrReturn)

  const [type, setType] = useState<string>('TROCA/DEVOLUÇÃO')
  const [currentIndex, setCurrentIndex] = useState<number>(0)

  const [showConfirmStartResolutionModal, setShowConfirmStartResolutionModal] = useState<boolean>(false)

  const [showDelayResolutionModal, setShowDelayResolutionModal] = useState<boolean>(false)

  const [inResolution, setInResolution] = useState<boolean>(false)

  const [showConfirmSupplierReleaseModal, setShowConfirmSupplierReleaseModal] = useState<boolean>(false)

  const [showGoBackOrInterruptModal, setShowGoBackOrInterruptModal] = useState<boolean>(false)
  const [interrupting, setInterrupting] = useState<boolean>(false)

  const [showFinishInvoiceModal, setShowFinishInvoiceModal] = useState<boolean>(false)

  const [preventDoubleClick, setPreventDoubleClick] = useState<boolean>(false)

  useEffect(() => {
    validateFc()
  }, [validateFc])

  useEffect(() => {
    if (useAs === 'page') {
      localStorage.removeItem('idReceivementFromFinish')
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (interrupting) {
      localStorage.removeItem('swapOrReturnToResolveIdsLS')
      localStorage.removeItem('swapOrReturnToResolveCurrentIndexLS')
      setInterrupting(false)
      return
    }
    if (swapOrReturnToResolveIds.length === 0) {
      const swapOrReturnToResolveIdsLS = localStorage.getItem('swapOrReturnToResolveIdsLS')
      if (swapOrReturnToResolveIdsLS) {
        dispatch(actions.setProperty({
          property: 'swapOrReturnToResolveIds',
          value: JSON.parse(swapOrReturnToResolveIdsLS),
        }))
        const currentIndexLS = localStorage.getItem('swapOrReturnToResolveCurrentIndexLS')
        if (currentIndexLS) {
          setCurrentIndex(parseInt(currentIndexLS, 10))
        }
      } else {
        toast.error('É necessário selecionar ao menos uma troca ou devolução para resolver!')
        return
      }
    }
    localStorage.setItem('swapOrReturnToResolveIdsLS', JSON.stringify(swapOrReturnToResolveIds))
    localStorage.setItem('swapOrReturnToResolveCurrentIndexLS', currentIndex.toString())
    dispatch(actions.getSwapOrReturnToResolve(swapOrReturnToResolveIds[currentIndex]))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, swapOrReturnToResolveIds])

  useEffect(() => {
    if (swapOrReturnToResolveData) {
      const typeName = SwapOrReturnTypesEnum[SwapOrReturnTypeIds[swapOrReturnToResolveData.idType]]
      setType(typeName.toUpperCase())
      if (swapOrReturnToResolveData.status === SwapOrReturnStatusEnum.CANCELED) {
        setInResolution(false)
      }
    }
  }, [swapOrReturnToResolveData])

  const allProductsCounted = useMemo(() => {
    if (swapOrReturnToResolveData && swapOrReturnToResolveData.idType === SwapOrReturnTypeIds.RETURN) {
      return swapOrReturnToResolveData.items.every((product) => product.notFound || product.returnQuantityCounted)
    }
    if (swapOrReturnToResolveData && swapOrReturnToResolveData.idType === SwapOrReturnTypeIds.SWAP) {
      return swapOrReturnToResolveData.items.every((product) => product.notFound || (product.returnQuantityCounted && !!product.receiveQuantityCounted))
    }
    return false
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swapOrReturnToResolveData?.items])

  const startResolution = () => {
    if (swapOrReturnToResolveData) {
      if (swapOrReturnToResolveData.status === SwapOrReturnStatusEnum.IN_RESOLUTION && !showConfirmStartResolutionModal) {
        setShowConfirmStartResolutionModal(true)
        return
      }

      setShowConfirmStartResolutionModal(false)

      dispatch(actions.startSwapOrReturnToResolve({
        id: swapOrReturnToResolveData.idRequest,
        idFulfillmentCenter: fc.id,
        onSuccess: () => setInResolution(true),
      }))
    }
  }

  const delayResolution = () => {
    if (swapOrReturnToResolveData) {
      if (!showDelayResolutionModal) {
        setShowDelayResolutionModal(true)
        return
      }

      const onSuccess = () => {
        setInResolution(false)
        setShowDelayResolutionModal(false)
      }

      dispatch(actions.delaySwapOrReturnToResolve({
        id: swapOrReturnToResolveData.idRequest,
        onSuccess,
      }))
    }
  }

  const supplierRelease = () => {
    if (preventDoubleClick) return

    if (!showConfirmSupplierReleaseModal) {
      setShowConfirmSupplierReleaseModal(true)
      return
    }

    setPreventDoubleClick(true)

    const defaultAction = () => {
      setShowConfirmSupplierReleaseModal(false)
      setPreventDoubleClick(false)
    }

    if (swapOrReturnToResolveData) {
      const onSuccess = (idsRequestsWaitingRelease: number[]) => {
        defaultAction()
        if (idsRequestsWaitingRelease.length > 0) {
          toast.success('Seguindo para liberação do fornecedor...')
          dispatch(actions.setProperty({
            property: 'pendingsToPrint',
            value: {
              supplierName: swapOrReturnToResolveData.supplierName,
              pendings: idsRequestsWaitingRelease.map((idRequest) => ({ idRequest })),
            },
          }))
          history.push('/invoices/release/swap-or-return')
        }
      }

      dispatch(actions.validateSupplierRelease({
        idCurrentRequest: swapOrReturnToResolveData.idRequest,
        idsSelectedRequests: swapOrReturnToResolveIds,
        onSuccess,
        onError: () => defaultAction(),
        hideErrorMessage: false,
      }))
    } else {
      defaultAction()
    }
  }

  const goBack = () => {
    if (!showGoBackOrInterruptModal) {
      setShowGoBackOrInterruptModal(true)
      return
    }

    if (swapOrReturnToResolveData && inResolution) {
      dispatch(actions.delaySwapOrReturnToResolve({
        id: swapOrReturnToResolveData.idRequest,
        onSuccess: () => null,
      }))
    }

    setShowGoBackOrInterruptModal(false)

    history.push('/swap-or-return/manage', { fromInternal: true })
  }

  const interruptResolutions = () => {
    if (!showGoBackOrInterruptModal) {
      setShowGoBackOrInterruptModal(true)
      return
    }

    const defaultAction = () => {
      setShowGoBackOrInterruptModal(false)
      setInterrupting(true)
      if (stopResolution) stopResolution()
    }

    if (swapOrReturnToResolveData) {
      const onSuccess = (idsRequestsWaitingRelease: number[]) => {
        defaultAction()
        if (idsRequestsWaitingRelease.length > 0) {
          toast.success('Pendências interrompidas com sucesso! Seguindo para liberação do fornecedor...')
          dispatch(actions.setProperty({
            property: 'pendingsToPrint',
            value: {
              supplierName: swapOrReturnToResolveData.supplierName,
              pendings: idsRequestsWaitingRelease.map((idRequest) => ({ idRequest })),
            },
          }))
          history.push('/invoices/release/swap-or-return')
        }
      }

      dispatch(actions.validateSupplierRelease({
        idCurrentRequest: swapOrReturnToResolveData.idRequest,
        idsSelectedRequests: swapOrReturnToResolveIds,
        onSuccess,
        onError: () => defaultAction(),
        hideErrorMessage: true,
      }))
    } else {
      defaultAction()
    }
  }

  const pagination = (goto: 'previous' | 'next') => {
    if (goto === 'previous') {
      setCurrentIndex(currentIndex - 1)
      localStorage.setItem('swapOrReturnToResolveCurrentIndexLS', (currentIndex - 1).toString())
    } else if (goto === 'next') {
      setCurrentIndex(currentIndex + 1)
      localStorage.setItem('swapOrReturnToResolveCurrentIndexLS', (currentIndex + 1).toString())
    }
  }

  const finishInvoice = () => {
    if (preventDoubleClick) {
      return
    }
    setPreventDoubleClick(true)
    if (swapOrReturnToResolveData) {
      if (!showFinishInvoiceModal) {
        setShowFinishInvoiceModal(true)
        setPreventDoubleClick(false)
        return
      }

      const onSuccess = (allFinished: boolean, pendings: PendingData[]) => {
        setShowFinishInvoiceModal(false)
        setInResolution(false)
        setPreventDoubleClick(false)
        if (allFinished) {
          if (pendings.length > 0) {
            toast.success('Todas as resoluções foram concluídas com sucesso! Seguindo para liberação de fornecedor...')
            dispatch(actions.setProperty({
              property: 'pendingsToPrint',
              value: {
                supplierName: swapOrReturnToResolveData.supplierName,
                pendings,
              },
            }))
            history.push('/invoices/release/swap-or-return')
          } else {
            toast.success('Todas as resoluções foram concluídas com sucesso!')
          }
        } else {
          toast.success('Resolução da nota concluída com sucesso! Siga para próxima!')
        }
      }

      dispatch(actions.finishSwapOrReturnToResolve({
        idRequest: swapOrReturnToResolveData.idRequest,
        idsRequests: swapOrReturnToResolveIds,
        onSuccess,
        onError: () => setPreventDoubleClick(false),
      }))
    }
    setPreventDoubleClick(false)
  }

  return (
    <>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Typography variant='h5' fontWeight='bold' data-testid='title-test-id'>{type}</Typography>
        <Box
          sx={{
            position: 'absolute',
            right: '0',
            marginRight: '1rem',
          }}
        >
          <Typography variant='h6'>{swapOrReturnToResolveIds.length === 0 ? '0/0' : `${currentIndex + 1}/${swapOrReturnToResolveIds.length}`}</Typography>
        </Box>
      </Box>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          gap: '4rem',
          marginTop: '.5rem',
          marginBottom: '1rem',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: '.5rem',
          }}
        >
          <Typography variant='h6' fontWeight='bold'>Fornecedor:</Typography>
          <Typography variant='h6'>{swapOrReturnToResolveData?.supplierName || '-'}</Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            gap: '.5rem',
          }}
        >
          <Typography variant='h6' fontWeight='bold'>NF:</Typography>
          <Typography variant='h6'>{swapOrReturnToResolveData?.invoiceAccessKey || '-'}</Typography>
        </Box>
        {(swapOrReturnToResolveData?.idType === SwapOrReturnTypeIds.RETURN && swapOrReturnToResolveData?.infoReturnOptions) && (
          <Typography variant='h6' fontWeight='bold'>Fornecedor {getReceivementAgreementInfo(swapOrReturnToResolveData?.infoReturnOptions).infoSupplier}</Typography>
        )}
      </Box>
      <Box sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
      }}
      >
        {!inResolution ? (
          <S.CustomButton
            title='Iniciar Resolução'
            type='button'
            onClick={startResolution}
            bgColor='blue'
            hoverColor='darkBlueHover'
            letterColor='white'
            hidden={!swapOrReturnToResolveData}
            testId='start-resolution-button-test-id'
          />
        ) : (
          <S.CustomButton
            title='Adiar Resolução'
            type='button'
            onClick={delayResolution}
            bgColor='orange3'
            hoverColor='darkOrangeHover'
            letterColor='white'
            testId='delay-resolution-button-test-id'
          />
        )}
        <S.StatusBox show={!!swapOrReturnToResolveData} status={swapOrReturnToResolveData?.status}>
          <Typography variant='body1'>{swapOrReturnToResolveData?.status}</Typography>
        </S.StatusBox>
        <S.CustomButton
          title='Liberar Fornecedor'
          type='button'
          onClick={supplierRelease}
          bgColor='orange3'
          hoverColor='darkOrangeHover'
          letterColor='white'
          hidden={!swapOrReturnToResolveData}
          testId='supplier-release-resolution-button-test-id'
        />
      </Box>
      {swapOrReturnToResolveData && (
      <ResolveSwapOrReturnTable
        swapOrReturnToResolve={swapOrReturnToResolveData}
        inResolution={inResolution}
        tableHeight={useAs === 'page' ? '56vh' : '44vh'}
      />
      )}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: '1rem',
        }}
      >
        {useAs === 'page' ? (
          <>
            <S.CustomButton
              title='Voltar'
              type='button'
              onClick={goBack}
              bgColor='blue'
              hoverColor='darkBlueHover'
              letterColor='white'
              testId='go-back-swap-or-return-button-test-id'
            />
            <ConfirmOrRejectModal
              show={showGoBackOrInterruptModal}
              title='Voltar'
              customizableDescription={(
                <S.BodyModalAlert>
                  <S.AlertMessageBox>
                    <Typography variant='body1'>
                      Ao voltar para a página anterior, a resolução atual será adiada.
                    </Typography>
                    <Typography variant='body1'>
                      Deseja continuar?
                    </Typography>
                  </S.AlertMessageBox>
                </S.BodyModalAlert>
              )}
              onConfirm={goBack}
              onClose={() => setShowGoBackOrInterruptModal(false)}
              onReject={() => setShowGoBackOrInterruptModal(false)}
              dataTestId='go-back-swap-or-return-modal-test-id'
            />
          </>
        ) : (
          <>
            <S.CustomButton
              title='Interromper Resolução'
              type='button'
              onClick={interruptResolutions}
              bgColor='red'
              hoverColor='redHover'
              letterColor='white'
              testId='interrupt-swap-or-return-button-test-id'
            />
            <ConfirmOrRejectModal
              show={showGoBackOrInterruptModal}
              title='Interromper'
              customizableDescription={(
                <S.BodyModalAlert>
                  <S.AlertMessageBox>
                    <Typography variant='body1'>
                      Ao interromper as resoluções, as pendências não concluídas serão adiadas.
                    </Typography>
                    <Typography variant='body1'>
                      Deseja continuar?
                    </Typography>
                  </S.AlertMessageBox>
                </S.BodyModalAlert>
              )}
              onConfirm={interruptResolutions}
              onClose={() => setShowGoBackOrInterruptModal(false)}
              onReject={() => setShowGoBackOrInterruptModal(false)}
              dataTestId='interrupt-swap-or-return-modal-test-id'
            />
          </>
        )}
        <Box
          sx={{
            display: 'flex',
            gap: '1rem',
          }}
        >
          <S.CustomButtonNavigate
            title='Anterior'
            type='button'
            onClick={() => pagination('previous')}
            bgColor='blue'
            hoverColor='darkBlueHover'
            letterColor='white'
            disabled={swapOrReturnToResolveIds.length === 0 || currentIndex === 0 || inResolution}
            testId='previous-swap-or-return-button-test-id'
          />
          <S.CustomButtonNavigate
            title='Próxima'
            type='button'
            onClick={() => pagination('next')}
            bgColor='blue'
            hoverColor='darkBlueHover'
            letterColor='white'
            disabled={swapOrReturnToResolveIds.length === 0 || currentIndex === swapOrReturnToResolveIds.length - 1 || inResolution}
            testId='next-swap-or-return-button-test-id'
          />
        </Box>
        <S.CustomButton
          title='Concluir Nota'
          type='button'
          onClick={finishInvoice}
          bgColor='confirm'
          hoverColor='confirmHover'
          letterColor='white'
          testId='finish-invoice-button-test-id'
          disabled={!inResolution || !allProductsCounted}
        />
        <ConfirmOrRejectModal
          show={showConfirmStartResolutionModal}
          title='Iniciar Resolução'
          customizableDescription={(
            <S.BodyModalAlert>
              <S.AlertMessageBox>
                <Typography variant='body1'>
                  Essa pendência já está em resolução.
                </Typography>
                <Typography variant='body1'>
                  Deseja continuar?
                </Typography>
              </S.AlertMessageBox>
            </S.BodyModalAlert>
          )}
          onConfirm={startResolution}
          onClose={() => setShowConfirmStartResolutionModal(false)}
          onReject={() => setShowConfirmStartResolutionModal(false)}
          dataTestId='start-resolution-modal-test-id'
        />
        <ConfirmOrRejectModal
          show={showDelayResolutionModal}
          title='Adiar Resolução'
          customizableDescription={(
            <S.BodyModalAlert>
              <S.AlertMessageBox>
                <Typography variant='body1'>
                  Ao realizar a ação, a pendência ira para o estado pendente.
                </Typography>
                <Typography variant='body1'>
                  Deseja continuar?
                </Typography>
              </S.AlertMessageBox>
            </S.BodyModalAlert>
          )}
          onConfirm={delayResolution}
          onClose={() => setShowDelayResolutionModal(false)}
          onReject={() => setShowDelayResolutionModal(false)}
          dataTestId='delay-resolution-modal-test-id'
        />
        <ConfirmOrRejectModal
          show={showFinishInvoiceModal}
          title='Concluir Nota'
          description='Deseja concluir a resolução da nota?'
          onConfirm={finishInvoice}
          onClose={() => setShowFinishInvoiceModal(false)}
          onReject={() => setShowFinishInvoiceModal(false)}
          dataTestId='finish-invoice-modal-test-id'
        />
        <ConfirmOrRejectModal
          show={showConfirmSupplierReleaseModal}
          title='Liberação de Fornecedor'
          customizableDescription={(
            <S.BodyModalAlert>
              <S.AlertMessageBox>
                <Typography variant='body1'>
                  Ao confirmar a ação, somente as notas já concluídas serão liberadas.
                </Typography>
                <Typography variant='body1'>
                  Obs: Notas ainda pendentes ou em resolução não serão liberadas.
                </Typography>
                <Typography variant='body1'>
                  Deseja continuar?
                </Typography>
              </S.AlertMessageBox>
            </S.BodyModalAlert>
          )}
          onConfirm={supplierRelease}
          onClose={() => setShowConfirmSupplierReleaseModal(false)}
          onReject={() => setShowConfirmSupplierReleaseModal(false)}
          dataTestId='supplier-release-modal-test-id'
        />
      </Box>
    </>
  )
}
